/**
 * Отправка отчётов об ошибках на бэк (client-webapp).
 *
 * Дизайн (важно, не ломать):
 * - Репортер использует СЫРОЙ fetch, НЕ api.request()/react-query. Иначе его собственная
 *   ошибка попадёт в queryCache.subscribe (__root) → бесконечная рекурсия отчётов.
 * - reportError НИКОГДА не бросает наружу (всё в try/catch) и не создаёт записей в кэше.
 * - report_code генерится СИНХРОННО на фронте и показывается юзеру мгновенно — даже если
 *   POST не дошёл. Тот же код летит на бэк → совпадает с строкой в БД (юзер диктует его нам).
 * - autoLog дедупит по fingerprint (cooldown) + глобальный лимит частоты — тысячи клиентов
 *   не должны задудосить бэк штормом ошибок.
 */

import { api, ApiError } from '@/lib/api';
import { APP_VERSION } from '@/lib/version';
import { getTgUser, getWebApp } from '@/lib/telegram';

const API_BASE = import.meta.env.VITE_API_BASE || '';
const ENDPOINT = '/api/client/errors';

const AUTO_COOLDOWN_MS = 60_000; // не чаще 1 авто-лога на один fingerprint в минуту
const MAX_AUTO_PER_MIN = 10; // глобальный потолок авто-логов (штормозащита)

// Crockford base32 без I/L/O/U — читается голосом и на скрине без путаницы.
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

export type ReportKind = 'auto' | 'user_report';
export type ReportSeverity = 'crash' | 'error';

export interface ReportResult {
  reportCode: string;
  ok: boolean;
}

export interface ReportOptions {
  kind: ReportKind;
  severity: ReportSeverity;
  description?: string;
  componentStack?: string;
}

/** Синхронно, без сети — код показываем юзеру сразу. */
export function generateReportCode(length = 8): string {
  const bytes = new Uint8Array(length);
  (globalThis.crypto ?? window.crypto).getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < length; i++) out += ALPHABET[bytes[i] % ALPHABET.length];
  return out;
}

function normalizeMessage(message: string): string {
  return message
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '#')
    .replace(/https?:\/\/\S+/g, '#')
    .replace(/0x[0-9a-f]+/gi, '#')
    .replace(/\d+/g, '#')
    .trim()
    .slice(0, 200);
}

/** Быстрый sync-хэш (djb2) — не crypto, нужен только для группировки повторов. */
function hashStr(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(16);
}

export function fingerprintOf(error: unknown): string {
  const err = error as { name?: string; message?: string; stack?: string } | null;
  const name = err?.name ?? '';
  const message = err?.message ?? String(error ?? '');
  let firstFrame = '';
  if (err?.stack) {
    for (const raw of err.stack.split('\n')) {
      const line = raw.trim();
      if (line.startsWith('at ') || line.includes('@') || line.includes('(')) {
        firstFrame = line;
        break;
      }
    }
  }
  return hashStr(`${name}|${normalizeMessage(message)}|${firstFrame}`);
}

function collectContext() {
  const tg = getWebApp();
  return {
    viewport: { w: window.innerWidth, h: window.innerHeight },
    tg_platform: tg?.platform,
    tg_version: tg?.version,
    color_scheme: tg?.colorScheme,
    // Загрузился ли SDK и пришла ли initData. Без этих двух флагов отказ «Telegram не
    // отвечает» неотличим от обычной ошибки: tg_user_id в таком отчёте пустой.
    tg_sdk_loaded: Boolean(tg),
    has_init_data: Boolean(tg?.initData),
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
  };
}

interface ErrorReportBody {
  report_code: string;
  kind: ReportKind;
  severity: ReportSeverity;
  message: string;
  error_name?: string;
  technical?: string;
  stack?: string;
  component_stack?: string;
  route: string;
  app_version: string;
  user_agent: string;
  tg_user_id?: number;
  context: Record<string, unknown>;
  user_description?: string;
  fingerprint: string;
}

function buildBody(error: unknown, opts: ReportOptions, reportCode: string): ErrorReportBody {
  const err = error as { name?: string; message?: string; stack?: string } | null;
  const isApi = error instanceof ApiError;
  return {
    report_code: reportCode,
    kind: opts.kind,
    severity: opts.severity,
    message: (err?.message ?? String(error ?? 'Unknown error')).slice(0, 4000),
    error_name: err?.name,
    technical: isApi ? (error as ApiError).technical : undefined,
    stack: err?.stack?.slice(0, 20000),
    component_stack: opts.componentStack?.slice(0, 20000),
    route: window.location.pathname + window.location.search,
    app_version: APP_VERSION,
    user_agent: navigator.userAgent.slice(0, 512),
    tg_user_id: getTgUser()?.id,
    context: collectContext(),
    user_description: opts.description?.slice(0, 4000) || undefined,
    fingerprint: fingerprintOf(error),
  };
}

/**
 * Отправить отчёт. Всегда возвращает reportCode (даже при сетевом сбое), никогда не бросает.
 */
export async function reportError(error: unknown, opts: ReportOptions): Promise<ReportResult> {
  const reportCode = generateReportCode();
  try {
    const body = buildBody(error, opts, reportCode);
    const token = api.getAuth().token;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}${ENDPOINT}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true, // отправка переживёт reload/закрытие вкладки
    });
    return { reportCode, ok: res.ok };
  } catch {
    // Сеть/бэк недоступны — код всё равно показываем юзеру.
    return { reportCode, ok: false };
  }
}

// ---- Авто-лог с дедупом и троттлингом ----
const lastAutoByFp = new Map<string, number>();
let autoWindow: number[] = [];

/**
 * Тихий авто-лог. Возвращает reportCode если реально отправили, иначе null (задедуплено/лимит).
 */
export async function autoLog(
  error: unknown,
  opts: Omit<ReportOptions, 'kind'> = { severity: 'error' },
): Promise<string | null> {
  const now = Date.now();
  const fp = fingerprintOf(error);

  const last = lastAutoByFp.get(fp);
  if (last && now - last < AUTO_COOLDOWN_MS) return null;

  autoWindow = autoWindow.filter((t) => now - t < 60_000);
  if (autoWindow.length >= MAX_AUTO_PER_MIN) return null;

  lastAutoByFp.set(fp, now);
  autoWindow.push(now);

  const { reportCode } = await reportError(error, { ...opts, kind: 'auto' });
  return reportCode;
}
